import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import useLivePoll from "@/hooks/useLivePoll";
import { Bell, CheckCheck } from "lucide-react";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const box = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications", { params: { limit: 20 } });
    setRows(data.notifications || []);
    setUnread(data.unread || 0);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);
  useLivePoll(() => load().catch(() => {}), 15000);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    load().catch(() => {});
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* noop */ }
    load().catch(() => {});
  };

  return (
    <div className="relative" ref={box}>
      <button onClick={() => setOpen((v) => !v)} data-testid="notification-bell"
              aria-label="Notifications"
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/25 text-white transition-colors hover:bg-white/10">
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notification-unread-count"
                className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-[18px] text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel"
             className="absolute right-0 z-50 mt-2 w-[min(92vw,360px)] border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Notifications</span>
            <button onClick={readAll} data-testid="notification-read-all"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#134e4a] hover:underline">
              <CheckCheck size={13} /> Mark all read
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {rows.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500" data-testid="notification-empty">
                Nothing new.
              </p>
            ) : rows.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
                      className={`block w-full border-b border-slate-100 px-3 py-2.5 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-teal-50/60"}`}>
                <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                <div className="mt-0.5 text-xs text-slate-600">{n.body}</div>
                <div className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">{fmtDateTime(n.at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
